import { useEffect, useRef, useState } from 'react'
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, Input, Button, Text
} from '@chakra-ui/react'
import { theme } from 'styles'

// One text field: used for new folder, rename folder and rename file.
// `onSubmit(name)` returns an error string to keep the modal open, or
// nothing to close it.
const NameModal = ({ isOpen, onClose, title, label = 'Name', initialValue = '', confirmLabel = 'Save', selectBaseName = false, onSubmit }) => {
  const [value, setValue] = useState(initialValue)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef()

  useEffect(() => {
    if (!isOpen) return
    setValue(initialValue)
    setError(null)
    setSaving(false)
    // Select after the new value has rendered and after Chakra's focus lock
    // has done its own focus; renaming "photo.jpg" starts with "photo"
    // selected so typing replaces the name and keeps the extension.
    const t = setTimeout(() => {
      const el = inputRef.current
      if (!el) return
      el.focus()
      const dot = selectBaseName ? initialValue.lastIndexOf('.') : -1
      el.setSelectionRange(0, dot > 0 ? dot : initialValue.length)
    }, 60)
    return () => clearTimeout(t)
  }, [isOpen, initialValue, selectBaseName])

  const submit = async () => {
    const name = value.trim()
    if (!name) { setError(`${label} is required`); return }
    setSaving(true)
    const err = await onSubmit(name)
    setSaving(false)
    if (err) setError(err)
    else onClose()
  }

  return (
    <Modal
      isOpen={isOpen} onClose={() => !saving && onClose()}
      initialFocusRef={inputRef}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isInvalid={Boolean(error)}>
            <FormLabel>{label}</FormLabel>
            <Input
              ref={inputRef}
              value={value}
              onChange={e => { setValue(e.target.value); setError(null) }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
              maxLength={200}
            />
            {error && <Text mt={2} fontSize="sm" color={theme.danger}>{error}</Text>}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>Cancel</Button>
          <Button colorScheme="green" onClick={submit} isLoading={saving}>{confirmLabel}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default NameModal
